import React, { useState, useRef, useEffect } from 'react';

const API = 'https://talku.online';
const AVATAR_COLORS = ['#6ee7b7','#93c5fd','#fca5a5','#fcd34d','#c4b5fd','#86efac','#fdba74','#67e8f9'];
const initials = (name) => (name||'?').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase();
const avatarColor = (name) => AVATAR_COLORS[(name||'').charCodeAt(0) % AVATAR_COLORS.length];

const TABS = ['Todos', 'Mensajes'];

const fmtRel = (ts) => {
  if (!ts) return '';
  try {
    const s = String(ts).replace(' ', 'T');
    const d = new Date(s.includes('+') || s.includes('Z') ? s : s + '-05:00');
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 1) return 'Justo ahora';
    if (diff < 60) return `Hace ${diff} min`;
    const h = Math.floor(diff / 60);
    if (h < 24) return `Hace ${h} h`;
    if (h < 48) return 'Ayer';
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

const channelLabel = (ch) => {
  if (ch === 'instagram') return 'Instagram';
  if (ch === 'messenger') return 'Messenger';
  return 'WhatsApp';
};

export default function NotificationPanel({ onClose }) {
  const [activeTab, setActiveTab] = useState('Todos');
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readIds, setReadIds] = useState(new Set());
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    fetch(`${API}/whatsapp/conversations`)
      .then(r => r.json())
      .then(data => {
        const convs = Array.isArray(data) ? data : (data.conversations || data.data || []);
        const msgs = convs
          .filter(c => (c.unread_count || 0) > 0)
          .slice(0, 30)
          .map(c => ({
            id: c.id,
            type: 'message',
            unread: true,
            name: c.contact_name || c.contact_phone || 'Contacto',
            channel: channelLabel(c.channel),
            time: fmtRel(c.last_message_at),
            text: c.last_message || '',
            count: c.unread_count || 1,
          }));
        setNotifs(msgs);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const isUnread = (n) => n.unread && !readIds.has(n.id);
  const markRead = (id) => setReadIds(prev => new Set([...prev, id]));
  const markAll = () => setReadIds(new Set(notifs.map(n => n.id)));
  const unreadCount = notifs.filter(n => isUnread(n)).length;

  const filtered = notifs.filter(n => {
    if (activeTab === 'Mensajes') return n.type === 'message';
    return true;
  });

  return (
    <>
      <style>{`
        .np-panel{position:absolute;top:calc(100% + 12px);right:0;width:390px;background:#fff;border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,0.14),0 4px 20px rgba(0,0,0,0.08);border:1px solid rgba(0,0,0,0.06);z-index:2000;overflow:hidden;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .np-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px 10px;}
        .np-title{font-size:17px;font-weight:700;color:#0a0a0a;letter-spacing:-0.3px;}
        .np-count{background:#0a0a0a;color:#fff;font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px;}
        .np-mark-all{font-size:12px;color:#888;background:none;border:none;cursor:pointer;font-family:inherit;padding:0;transition:color 0.15s;}
        .np-mark-all:hover{color:#0a0a0a;}
        .np-tabs{display:flex;gap:6px;padding:0 20px 12px;}
        .np-tab{padding:5px 13px;border-radius:999px;font-size:12.5px;font-weight:500;cursor:pointer;transition:all 0.15s;font-family:inherit;white-space:nowrap;line-height:1.4;}
        .np-tab.active{background:#0a0a0a;color:#fff;border:1px solid #0a0a0a;}
        .np-tab:not(.active){background:transparent;color:#777;border:1px solid #E5E7EB;}
        .np-tab:not(.active):hover{background:#F9FAFB;color:#333;}
        .np-divider{height:1px;background:#F3F4F6;}
        .np-list{max-height:450px;overflow-y:auto;}
        .np-list::-webkit-scrollbar{width:0;}
        .np-item{display:flex;gap:12px;padding:14px 20px;cursor:pointer;transition:background 0.15s;border-bottom:1px solid #F9FAFB;}
        .np-item:last-child{border-bottom:none;}
        .np-item:hover{background:#F9FAFB;}
        .np-item.unread{background:#fafffe;}
        .np-item.unread:hover{background:#f2fefc;}
        .np-av-wrap{position:relative;flex-shrink:0;}
        .np-avatar{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#555;}
        .np-dot{position:absolute;top:-2px;left:-2px;width:10px;height:10px;border-radius:50%;background:#22C55E;border:2px solid #fff;}
        .np-content{flex:1;min-width:0;}
        .np-item-top{font-size:13px;line-height:1.45;margin-bottom:3px;}
        .np-item-name{font-weight:700;color:#0a0a0a;}
        .np-item-action{color:#555;}
        .np-badge{display:inline-flex;align-items:center;gap:3px;padding:2px 8px;background:#F9FAFB;border:1px solid #E5E7EB;border-radius:999px;font-size:11px;font-weight:500;color:#555;vertical-align:middle;margin-left:2px;}
        .np-meta{font-size:11px;color:#9CA3AF;margin-bottom:8px;}
        .np-comment{padding:9px 12px;background:#F5F5F5;border-radius:12px;font-size:12.5px;color:#555;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
        .np-unread-badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;background:#22C55E;color:#fff;font-size:10px;font-weight:700;border-radius:999px;margin-left:6px;vertical-align:middle;}
        .np-empty{padding:44px 20px;text-align:center;color:#9CA3AF;font-size:13px;}
        .np-loading{padding:44px 20px;text-align:center;}
        .np-spinner{width:22px;height:22px;border:2px solid #f0f0f0;border-top:2px solid #0a0a0a;border-radius:50%;animation:np-spin 0.7s linear infinite;margin:0 auto;}
        @keyframes np-spin{to{transform:rotate(360deg)}}
      `}</style>
      <div ref={ref} className="np-panel" onClick={e => e.stopPropagation()}>

        <div className="np-header">
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span className="np-title">Notificaciones</span>
            {unreadCount > 0 && <span className="np-count">{unreadCount}</span>}
          </div>
          {unreadCount > 0 && (
            <button className="np-mark-all" onClick={markAll}>Marcar todo leído</button>
          )}
        </div>

        <div className="np-tabs">
          {TABS.map(tab => (
            <button key={tab} className={`np-tab${activeTab===tab?' active':''}`} onClick={() => setActiveTab(tab)}>
              {tab}
            </button>
          ))}
        </div>
        <div className="np-divider"/>

        <div className="np-list">
          {loading ? (
            <div className="np-loading"><div className="np-spinner"/></div>
          ) : filtered.length === 0 ? (
            <div className="np-empty">Sin mensajes sin leer</div>
          ) : filtered.map(n => (
            <div key={n.id} className={`np-item${isUnread(n)?' unread':''}`} onClick={() => markRead(n.id)}>
              <div className="np-av-wrap">
                <div className="np-avatar" style={{background:avatarColor(n.name)}}>{initials(n.name)}</div>
                {isUnread(n) && <div className="np-dot"/>}
              </div>
              <div className="np-content">
                <div className="np-item-top">
                  <span className="np-item-name">{n.name}</span>
                  <span className="np-item-action"> envió {n.count > 1 ? `${n.count} mensajes` : 'un mensaje'} en </span>
                  <span className="np-badge">{n.channel}</span>
                  {n.count > 1 && <span className="np-unread-badge">{n.count}</span>}
                </div>
                <div className="np-meta">{n.time} · Mensaje</div>
                {n.text && <div className="np-comment">{n.text}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
